<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import MediaGallery from '../components/MediaGallery.vue'
import { useShowroomStore } from '../store/showroom'

const store = useShowroomStore()
const route = useRoute()
const entity = computed(() => store.getEntity(String(route.params.id)))

const product = computed(() =>
  entity.value?.contentType === 'product' ? entity.value : null
)
const style = computed(() =>
  entity.value?.contentType === 'software-style' ? entity.value : null
)
const component = computed(() =>
  entity.value?.contentType === 'software-component' ? entity.value : null
)
const projectCase = computed(() =>
  entity.value?.contentType === 'project-case' ? entity.value : null
)
const relatedEntities = computed(() =>
  projectCase.value?.relatedEntityIds
    .map((id) => store.getEntity(id))
    .filter((item) => item !== undefined) ?? []
)
</script>

<template>
  <div v-if="entity" class="detail-layout" :class="{ 'case-detail-layout': projectCase }">
    <MediaGallery
      v-if="entity.mediaIds.length"
      :media-ids="entity.mediaIds"
      :title="entity.title"
    />
    <section v-else-if="projectCase" class="case-hero-visual" aria-label="商超楼宇自控系统概念示意">
      <div class="case-hero-grid"></div>
      <div class="case-building">
        <i v-for="index in 30" :key="index"></i>
      </div>
      <span class="case-kicker">RETAIL · BA CONTROL</span>
      <div class="case-hero-copy">
        <small>必服信息 · 行业解决方案样板</small>
        <strong>商超楼宇<br />自控系统</strong>
        <span>舒适环境 · 稳定运行 · 精细用能</span>
      </div>
      <div class="case-system-chips">
        <span>空调冷热源</span>
        <span>送排风</span>
        <span>照明时序</span>
        <span>能耗监测</span>
      </div>
      <b>概念示意 / 非真实项目现场</b>
    </section>
    <section v-else class="detail-visual">
      <span class="detail-code">{{ entity.contentType.toUpperCase() }}</span>
      <div class="detail-orbit"></div>
      <strong>{{ entity.systemDomains.join(' / ').toUpperCase() }}</strong>
      <small>已支持素材接入，当前内容暂未配置审核图片</small>
    </section>

    <section class="detail-content">
      <p class="eyebrow">CONTENT DETAIL</p>
      <h2>{{ entity.title }}</h2>
      <p class="detail-summary">{{ entity.summary }}</p>

      <div class="detail-badges">
        <span class="badge">{{ entity.rightsLevel }}</span>
        <span v-for="domain in entity.systemDomains" :key="domain" class="badge">
          {{ domain.toUpperCase() }}
        </span>
        <span v-if="projectCase?.demoData" class="badge badge-warning">
          虚构演示数据
        </span>
      </div>

      <div v-if="projectCase?.projectFacts?.length" class="case-project-facts">
        <article v-for="fact in projectCase.projectFacts" :key="fact.label">
          <span>{{ fact.label }}</span>
          <strong>{{ fact.value }}</strong>
          <small v-if="fact.description">{{ fact.description }}</small>
        </article>
      </div>

      <div v-if="product" class="detail-section">
        <h3>核心特点</h3>
        <ul class="feature-list">
          <li v-for="item in product.highlights" :key="item">{{ item }}</li>
        </ul>

        <h3>产品参数</h3>
        <dl v-if="product.parameters.length" class="parameter-list">
          <div v-for="parameter in product.parameters" :key="parameter.label">
            <dt>{{ parameter.label }}</dt>
            <dd>{{ parameter.value }}</dd>
          </div>
        </dl>
        <p v-else class="muted">正式参数待提供。</p>
      </div>

      <div v-if="style" class="detail-section">
        <h3>风格定义</h3>
        <dl class="parameter-list">
          <div>
            <dt>主题方向</dt>
            <dd>{{ style.themeLabel }}</dd>
          </div>
          <div>
            <dt>方案完整度</dt>
            <dd>{{ style.pagePreviews.length }}/10 个业务画面，可逐页讲解功能</dd>
          </div>
        </dl>
        <ul class="feature-list">
          <li v-for="item in style.highlights" :key="item">{{ item }}</li>
        </ul>
      </div>

      <div v-if="component" class="detail-section">
        <h3>组件价值</h3>
        <dl class="parameter-list">
          <div>
            <dt>解决问题</dt>
            <dd>{{ component.problem }}</dd>
          </div>
          <div>
            <dt>候选价值</dt>
            <dd>{{ component.value }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="projectCase" class="detail-section">
        <h3>案例概况</h3>
        <dl class="parameter-list">
          <div>
            <dt>建筑类型</dt>
            <dd>{{ projectCase.buildingType }}</dd>
          </div>
          <div>
            <dt>案例定位</dt>
            <dd>{{ projectCase.locationLabel }}</dd>
          </div>
          <div>
            <dt>客户问题</dt>
            <dd>{{ projectCase.challenge }}</dd>
          </div>
          <div>
            <dt>方案</dt>
            <dd>{{ projectCase.solution }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="projectCase" class="detail-section">
        <h3>方案价值</h3>
        <ul class="case-outcome-list">
          <li v-for="(outcome, index) in projectCase.outcomes" :key="outcome">
            <span>0{{ index + 1 }}</span>
            <p>{{ outcome }}</p>
          </li>
        </ul>
      </div>

      <div v-if="projectCase?.controlDescriptions?.length" class="detail-section">
        <h3>主要控制描述</h3>
        <div class="case-control-list">
          <article
            v-for="(control, index) in projectCase.controlDescriptions"
            :key="control.system"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <div>
              <strong>{{ control.system }}</strong>
              <p>{{ control.strategy }}</p>
            </div>
          </article>
        </div>
      </div>

      <div v-if="projectCase && relatedEntities.length" class="detail-section">
        <h3>推荐系统组合</h3>
        <div class="case-related-grid">
          <RouterLink
            v-for="item in relatedEntities"
            :key="item.id"
            :to="{ name: item.contentType === 'software-style' ? 'software-demo' : 'item', params: { id: item.id } }"
          >
            <span>{{ item.contentType === 'software-style' ? '软件风格' : '控制设备' }}</span>
            <strong>{{ item.title }}</strong>
            <small>查看内容 →</small>
          </RouterLink>
        </div>
      </div>

      <div v-if="projectCase" class="detail-section">
        <p v-if="projectCase.demoData" class="warning-note">
          当前为商超行业方案演示样板，数据与场景均为虚构，不代表真实客户、真实项目成果或节能承诺。后续替换为正式案例时，须完成客户授权、图片脱敏与技术审核。
        </p>
        <p v-else class="case-source-note">
          项目信息与主图来自本次提供的必服项目宣传素材；具体控制点位、节能数据和验收指标以经审核的项目资料为准。
        </p>
      </div>

      <div class="detail-actions">
        <button class="button button-primary" type="button" @click="store.addToDraft(entity)">
          ＋ 加入本次方案
        </button>
        <RouterLink class="button button-secondary" :to="{ name: 'basket' }">
          查看方案篮
        </RouterLink>
      </div>
    </section>
  </div>

  <div v-else class="empty-state">
    <strong>内容不存在或已撤回</strong>
    <RouterLink class="button button-secondary" :to="{ name: 'home' }">
      返回首页
    </RouterLink>
  </div>
</template>
